import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useUpdatePluginMeta } from "@/api/mutations/plugins";
import { repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import type { Plugin } from "@/types";

// #292 (UC-4): Meta (Beschreibung/Version) ändern — der Server macht daraus IMMER einen
// Entwurf (der Katalog ist governed). Der Erfolgstext sagt ehrlich, WO die Änderung jetzt
// liegt — ein „gespeichert" ohne den Entwurfs-Hinweis wäre gelogen (die Live-Meta ist
// unverändert, bis der Entwurf freigegeben wird).
export function UpdatePluginMetaDialog({
  open,
  onOpenChange,
  plugin,
  inAufnahme,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plugin: Plugin;
  /** true = das Plugin ist selbst noch in Aufnahme → gepatcht wird sein Aufnahme-Entwurf. */
  inAufnahme: boolean;
}) {
  const update = useUpdatePluginMeta();
  const [description, setDescription] = useState(plugin.description);
  const [version, setVersion] = useState(plugin.version ?? "");

  useEffect(() => {
    if (open) {
      setDescription(plugin.description);
      setVersion(plugin.version ?? "");
    }
  }, [open, plugin.description, plugin.version]);

  const dirtyDescription = description !== plugin.description;
  const dirtyVersion = version !== (plugin.version ?? "");
  const dirty = dirtyDescription || (dirtyVersion && version.trim() !== "");

  function submit() {
    update.mutate(
      {
        marketplaceId: plugin.marketplaceId,
        pluginName: plugin.name,
        // nur die GEÄNDERTEN Felder schicken — der Server patcht, er ersetzt nicht
        ...(dirtyDescription ? { description } : {}),
        ...(dirtyVersion && version.trim() !== "" ? { version: version.trim() } : {}),
      },
      {
        onSuccess: (res) => {
          // #292 (Review LOW-9): die SERVER-Antwort ist die Wahrheit, nicht der
          // Client-Zustand beim Öffnen (das Plugin kann zwischenzeitlich freigegeben sein).
          // Zyklus 2 (LOW-F): auch der submitted-Zweig hängt NICHT am Client-Zustand —
          // der neutrale Text ist in beiden Fällen (Aufnahme- wie Meta-Entwurf) wahr.
          toast.success(
            res.mode === "draft-created"
              ? "Als Entwurf eingereicht — die Änderung wird mit der Freigabe live."
              : res.submitted
                ? "Im eingereichten Entwurf aktualisiert — wird mit dessen Freigabe live."
                : "Im offenen Entwurf aktualisiert — er ist noch NICHT eingereicht (im Datei-Panel einreichen).",
          );
          onOpenChange(false);
        },
        onError: (err) => toast.error(repoErrorMessage(err, "Meta-Änderung fehlgeschlagen")),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="update-meta-dialog">
        <DialogHeader>
          <DialogTitle>Meta bearbeiten</DialogTitle>
          <DialogDescription>
            Beschreibung und Version des Katalog-Eintrags von „{plugin.name}".{" "}
            {inAufnahme
              ? "Das Plugin ist in Aufnahme — die Änderung landet direkt in seinem Aufnahme-Entwurf."
              : "Die Änderung wird als Entwurf eingereicht und ist erst nach der Freigabe live."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="meta-description">Beschreibung</Label>
            <Textarea
              id="meta-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              data-testid="meta-description"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="meta-version">Version (Manifest)</Label>
            <Input
              id="meta-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="z.B. 1.2.0"
              maxLength={64}
              data-testid="meta-version"
            />
            <p className="text-[11.5px] text-text3">
              Autor-Angabe im Katalog-Eintrag — nicht zu verwechseln mit der Repo-Version (Tag) in der Datei-Ansicht.
              Leer lassen = unverändert; ein Entfernen der Version gibt es (noch) nicht.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={!dirty || update.isPending} data-testid="meta-save">
            {inAufnahme ? "Im Entwurf aktualisieren" : "Als Entwurf einreichen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
